/**
 * Die Grundtypen des Relay-Schemas, so wie sie dort festgelegt sind
 * (`RELAY/docs/schema/METIS-schema.md`, Abschnitte „Logische Typen" und „Datum und Zeit").
 *
 * Zeiten sind **immer** `char(X)` ohne Trennzeichen und ohne Zeitzonensuffix: feste Länge,
 * lexikografisch sortierbar. Umgerechnet wird an der Grenze zur Datenbank, nicht in der App —
 * innerhalb der Oberfläche bleibt `Date` das Mittel der Wahl.
 */

const pad = (value: number, width = 2) => String(value).padStart(width, '0');

/** `char(17)` — `yyyyMMddHHmmssSSS`, Ortszeit des Geräts. */
export function toRelayDateTime(d: Date): string {
  return (
    d.getFullYear() +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) +
    pad(d.getHours()) +
    pad(d.getMinutes()) +
    pad(d.getSeconds()) +
    pad(d.getMilliseconds(), 3)
  );
}

/**
 * Abweichung von UTC als `+HH:MM` oder `-HH:MM`.
 *
 * Gehört **nicht** ins Relay-Format — dort sind Zeiten `char(X)` ohne Zone. Für eine
 * Beglaubigung ist eine Zeit ohne Zone aber kein Beleg, deshalb wird die Abweichung dort als
 * eigenes Feld mitgeführt (s. `platform/signature.ts`).
 */
export function utcOffset(d: Date): string {
  // `getTimezoneOffset` zählt andersherum: für UTC+2 liefert es -120.
  const minuten = -d.getTimezoneOffset();
  const zeichen = minuten < 0 ? '-' : '+';
  const abs = Math.abs(minuten);
  return zeichen + pad(Math.floor(abs / 60)) + ':' + pad(abs % 60);
}

/** `char(8)` — `yyyyMMdd`. */
export function toRelayDate(d: Date): string {
  return String(d.getFullYear()) + pad(d.getMonth() + 1) + pad(d.getDate());
}

export function fromRelayDateTime(value: string): Date {
  const n = (from: number, length: number) => Number(value.slice(from, from + length));
  return new Date(n(0, 4), n(4, 2) - 1, n(6, 2), n(8, 2), n(10, 2), n(12, 2), n(14, 3));
}
